
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Radar, Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ArcElement,
} from 'chart.js';
import { AiAnalysisKpis, Kpi } from '../../types';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ArcElement
);

interface AnalysisChartsProps {
    kpis: AiAnalysisKpis;
}

export interface AnalysisChartsRef {
    getChartImagesAsBase64: () => Record<string, string>;
}

const formatKpiKey = (key: string): string => {
    return key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const getScoreColor = (score: number, opacity: number = 1) => {
    if (score >= 8) return `rgba(74, 222, 128, ${opacity})`; // green-400
    if (score >= 5) return `rgba(250, 204, 21, ${opacity})`; // yellow-400
    return `rgba(248, 113, 113, ${opacity})`; // red-400
};

const AnalysisCharts = forwardRef<AnalysisChartsRef, AnalysisChartsProps>(({ kpis }, ref) => {
    const radarRef = useRef<ChartJS<'radar'>>(null);
    const barRef = useRef<ChartJS<'bar'>>(null);
    const pieRef = useRef<ChartJS<'pie'>>(null);
    const lineRef = useRef<ChartJS<'line'>>(null);

    // Dynamic extraction of scored KPIs
    const scoredKpis = Object.entries(kpis).filter(
        ([, value]) => typeof value === 'object' && value !== null && 'score' in value && typeof (value as any).score === 'number'
    ) as [string, Kpi][];

    useImperativeHandle(ref, () => ({
        getChartImagesAsBase64: () => {
            const images: Record<string, string> = {};
            const chartRefs = {
                radar: radarRef.current,
                bar: barRef.current,
                pie: pieRef.current,
                line: lineRef.current,
            };

            for (const [key, chart] of Object.entries(chartRefs)) {
                if (chart) {
                    const originalAnimation = chart.options.animation;
                    chart.options.animation = false;
                    chart.update('none');
                    images[key] = chart.toBase64Image();
                    chart.options.animation = originalAnimation;
                    chart.update('none');
                }
            }
            return images;
        }
    }));

    if (scoredKpis.length === 0) {
        return null;
    }

    const labels = scoredKpis.map(([key]) => formatKpiKey(key));
    const scores = scoredKpis.map(([, kpi]) => kpi.score);

    const commonChartOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                font: {
                    size: 16
                },
                color: '#e5e7eb', // gray-200
                padding: {
                    bottom: 15
                }
            }
        },
        animation: {
            duration: 1000,
            easing: 'easeOutCubic' as const,
        }
    };

    // --- Radar Chart ---
    const radarData = {
        labels,
        datasets: [{
            label: 'KPI Score',
            data: scores,
            backgroundColor: 'rgba(59, 130, 246, 0.2)',
            borderColor: 'rgba(59, 130, 246, 1)',
            borderWidth: 2,
            pointBackgroundColor: 'rgba(59, 130, 246, 1)',
            pointBorderColor: '#fff',
        }],
    };
    const radarOptions = {
        ...commonChartOptions,
        plugins: { ...commonChartOptions.plugins, title: { ...commonChartOptions.plugins.title, text: 'Rendimiento General' } },
        scales: {
            r: {
                angleLines: { color: 'rgba(255, 255, 255, 0.2)' },
                grid: { color: 'rgba(255, 255, 255, 0.2)' },
                pointLabels: { color: '#e5e7eb', font: { size: 12 } },
                ticks: { color: '#9ca3af', backdropColor: 'rgba(0, 0, 0, 0.5)', stepSize: 2 },
                suggestedMin: 0,
                suggestedMax: 10,
            },
        },
    };

    // --- Bar Chart ---
    const barData = {
        labels,
        datasets: [{
            label: 'Score',
            data: scores,
            backgroundColor: scores.map(score => getScoreColor(score, 0.6)),
            borderColor: scores.map(score => getScoreColor(score, 1)),
            borderWidth: 1,
        }],
    };
    const barOptions = {
        ...commonChartOptions,
        plugins: { ...commonChartOptions.plugins, title: { ...commonChartOptions.plugins.title, text: 'Comparación de Puntuaciones' } },
        scales: {
            y: {
                beginAtZero: true,
                max: 10,
                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                ticks: { color: '#9ca3af' },
            },
            x: {
                grid: { display: false },
                ticks: { color: '#9ca3af' },
            },
        },
    };
    
    // --- Pie Chart ---
    const scoreCategories = scores.reduce((acc, score) => {
        if (score >= 8) acc.good++;
        else if (score >= 5) acc.average++;
        else acc.poor++;
        return acc;
    }, { good: 0, average: 0, poor: 0 });

    const pieData = {
        labels: ['Bueno (8-10)', 'Promedio (5-7)', 'Pobre (1-4)'],
        datasets: [{
            data: [scoreCategories.good, scoreCategories.average, scoreCategories.poor],
            backgroundColor: [
                getScoreColor(8, 0.7),
                getScoreColor(6, 0.7),
                getScoreColor(3, 0.7),
            ],
            borderColor: [
                getScoreColor(8, 1),
                getScoreColor(6, 1),
                getScoreColor(3, 1),
            ],
            borderWidth: 1,
        }],
    };
    const pieOptions = {
        ...commonChartOptions,
        plugins: {
             ...commonChartOptions.plugins,
             title: { ...commonChartOptions.plugins.title, text: 'Distribución de Puntuaciones' },
             legend: {
                display: true,
                position: 'bottom' as const,
                labels: { color: '#e5e7eb' }
             }
        }
    };

    // --- Line Chart ---
    const lineData = {
        labels,
        datasets: [{
            label: 'Score',
            data: scores,
            fill: true,
            backgroundColor: 'rgba(59, 130, 246, 0.2)',
            borderColor: 'rgba(59, 130, 246, 1)',
            tension: 0.3,
        }],
    };
    const lineOptions = {
        ...commonChartOptions,
        plugins: { ...commonChartOptions.plugins, title: { ...commonChartOptions.plugins.title, text: 'Tendencia de Puntuaciones' } },
        scales: {
            y: {
                beginAtZero: true,
                max: 10,
                grid: { color: 'rgba(255, 255, 255, 0.1)' },
                ticks: { color: '#9ca3af' },
            },
            x: {
                grid: { display: false },
                ticks: { color: '#9ca3af' },
            },
        },
    };

    return (
        <div className="bg-gray-800/50 p-4 rounded-lg mb-4">
            <h3 className="font-semibold text-xl text-white mb-6 text-center">Análisis Visual de KPIs</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="relative h-80 bg-gray-900/50 p-4 rounded-lg">
                    <Radar ref={radarRef} data={radarData} options={radarOptions as any} />
                </div>
                <div className="relative h-80 bg-gray-900/50 p-4 rounded-lg">
                    <Bar ref={barRef} data={barData} options={barOptions as any} />
                </div>
                <div className="relative h-80 bg-gray-900/50 p-4 rounded-lg">
                    <Pie ref={pieRef} data={pieData} options={pieOptions as any} />
                </div>
                <div className="relative h-80 bg-gray-900/50 p-4 rounded-lg">
                    <Line ref={lineRef} data={lineData} options={lineOptions as any} />
                </div>
            </div>
        </div>
    );
});

export default AnalysisCharts;
